import { useCallback, useEffect, useRef, useState } from 'react';
import { captureException } from '@sentry/react';

const pushServerPublicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

const useWebPush = (postUrl) => {
    const request = () => {
        if ('serviceWorker' in navigator && 'PushManager' in window) {
            navigator
                .serviceWorker
                .register('/service-worker.js')
                .then(swReg => {
                    console.log('Service Worker Registered', swReg);
                })
                .catch(error => {
                    captureException(error);
                    console.error('Service Worker Register Error', error);
                });

            navigator
                .serviceWorker
                .ready
                .then(swReg => {
                        console.log('service worker ready');
                        return swReg.pushManager.getSubscription()
                            .then(subscription => {
                                if (subscription === null) {
                                    const applicationServerKey = urlBase64ToUint8Array(pushServerPublicKey);
                                    return swReg.pushManager.subscribe({
                                        userVisibleOnly: true,
                                        applicationServerKey: applicationServerKey
                                    });
                                } else {
                                    return subscription;
                                }
                            });
                    }
                )
                .then(subscription => {
                    if (!localStorage.getItem('has_pushed')){
                        return axios.post(postUrl, subscription).then(() => localStorage.setItem('has_pushed', '1'))
                    }
                    return true;
                }).catch(error => {
                console.error('Service Worker Error', error);
                if (error.code !== 0) {
                    captureException(error);
                }
            })
        }
    }

    return {
        request: request
    }
}
const CAST_NAMESPACE = 'urn:x-cast:app.lense.livewall';
const CAST_SENDER_SDK_URL = 'https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1';

const useGoogleCast = (castAppId) => {
    const [available, setAvailable] = useState(false);
    const [connected, setConnected] = useState(false);
    const [deviceName, setDeviceName] = useState(null);
    const contextRef = useRef(null);

    useEffect(() => {
        if (!castAppId) return;

        let context = null;
        let handleSessionStateChanged = null;

        const initContext = () => {
            if (!window.cast?.framework) return;

            context = cast.framework.CastContext.getInstance();
            contextRef.current = context;
            context.setOptions({
                receiverApplicationId: castAppId,
                autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
            });

            const respondToHello = (namespace, rawMessage) => {
                // The Cast SDK's message payload shape isn't consistent across
                // versions/platforms — sometimes a JSON string, sometimes an
                // already-parsed object. Handle both so a format mismatch
                // can't silently swallow the handshake.
                let message = rawMessage;
                if (typeof rawMessage === 'string') {
                    try {
                        message = JSON.parse(rawMessage);
                    } catch {
                        return;
                    }
                }

                if (message?.type !== 'hello') return;

                context.getCurrentSession()
                    ?.sendMessage(CAST_NAMESPACE, { url: window.location.href })
                    .catch(() => {});
            };

            // Only SESSION_STARTED/SESSION_RESUMED mean a session is actually
            // usable — getCurrentSession() can already return a (not yet
            // connected) session object during SESSION_STARTING, which would
            // otherwise show "connected" in the UI before the cast to the
            // device has really succeeded.
            const syncFromCurrentSession = (sessionState) => {
                const session = context.getCurrentSession();
                const isActive = sessionState == null
                    ? !!session
                    : sessionState === cast.framework.SessionState.SESSION_STARTED
                        || sessionState === cast.framework.SessionState.SESSION_RESUMED;

                setConnected(isActive);
                setDeviceName(isActive ? (session?.getCastDevice()?.friendlyName ?? null) : null);
            };

            handleSessionStateChanged = (event) => {
                syncFromCurrentSession(event.sessionState);

                if (event.sessionState === cast.framework.SessionState.SESSION_START_FAILED) {
                    console.error('[cast] session start failed', event);
                }

                const isStarting = event.sessionState === cast.framework.SessionState.SESSION_STARTED
                    || event.sessionState === cast.framework.SessionState.SESSION_RESUMED;

                // The receiver announces itself with a "hello" once its message
                // listener is actually registered — sending the url immediately
                // on session start can race the receiver page still loading.
                if (isStarting) {
                    context.getCurrentSession()?.addMessageListener(CAST_NAMESPACE, respondToHello);
                }
            };

            context.addEventListener(cast.framework.CastContextEventType.SESSION_STATE_CHANGED, handleSessionStateChanged);
            syncFromCurrentSession();
            setAvailable(true);
        };

        window.__onGCastApiAvailable = (isAvailable) => {
            if (isAvailable) initContext();
        };

        if (!document.getElementById('cast-sender-sdk')) {
            const script = document.createElement('script');
            script.id = 'cast-sender-sdk';
            script.src = CAST_SENDER_SDK_URL;
            document.head.appendChild(script);
        } else if (window.cast?.framework) {
            initContext();
        }

        return () => {
            if (context && handleSessionStateChanged) {
                context.removeEventListener(cast.framework.CastContextEventType.SESSION_STATE_CHANGED, handleSessionStateChanged);
            }
        };
    }, [castAppId]);

    const requestSession = useCallback(() => {
        contextRef.current?.requestSession().catch(() => {});
    }, []);

    const endSession = useCallback(() => {
        contextRef.current?.endCurrentSession(true);
    }, []);

    return { available, connected, deviceName, requestSession, endSession };
};

export {useWebPush, useGoogleCast}
